const log = (...args: unknown[]): void =>
    console.debug("[rke:text-settings]", ...args);

type ApparatusKey = "App1" | "App2" | "App3" | "App4";
type HighlightApparatusKey = "App1" | "App2" | "App3";

type CriticalApparatusSettings = Record<ApparatusKey, boolean>;
type HighlightCriticalApparatusSettings = Record<
    HighlightApparatusKey,
    boolean
>;

type TextFilterFunction = (
    textSettings: TextSettings,
    htmlRoot: HTMLElement,
) => void;

type FilterRegistry = {
    [K in
        | "applyAllInterlinearVariantsFilter"
        | "applyCharacterNormalizationFilter"
        | "applyCommentsFilter"
        | "applyCriticalApparatusFilter"
        | "applyCriticalApparatusHighlighter"
        | "applyDiacriticsModeDisplayFilter"
        | "applyDiacriticsModeHighlightFilter"
        | "applyEmphasizeInitialsFilter"
        | "applyExpandAbbreviationsFilter"
        | "applyHighlightPersonNamesFilter"
        | "applyHighlightPlaceNamesFilter"
        | "applyHighlightSubjectReferencesFilter"
        | "applyMedievalPunctuationFilter"
        | "applyLabelTitelsFilter"
        | "applyMisspellingsFilter"
        | "applyModernPunctuationFilter"
        | "applyOriginalCapitalizationFilter"
        | "applyOriginalWordBoundariesFilter"
        | "applySuppliedInsertFilter"
        | "applySuppliedMissingIllegibleFilter"
        | "applySwappedVersesFilter"
        | "applyProseVersionFilter"
        | "applyUncountedVersesFilter"]: TextFilterFunction;
};

/**
 * The TextFilter class is a reactive container for text display configuration settings, that automatically updates
 * a given DOM tree when a configuration setting changes.
 * To use the class you initialize it in the follow way:
 * 1) Instantiate an object with new TextFilter()
 * 2) Set the htmlRoot property to point to some node in a DOM tree. All the filtering will be applied to the children
 *    of the given root node
 * 3) If you want to initially filter the DOM tree with the default values of the text configuration, call
 *    applyAllFiltersToHtml()
 *
 * The display configuration settings can be accessed through the settings property. These properties are reactive, i.e.
 * when a property value changes, the corresponding filter function will be called and applied to the DOM tree in htmlRoot.
 *
 * The filter rules are currently hard-coded and apply to html elements and class attributes that are generated by the
 * script /build/tei-to-html.xsl at build time. This script basically takes the element names of the source TEI documents
 * and uses those element names as class attributes on the generated HTML.
 */
class TextFilter {
    constructor() {}

    /* The filter registry is used to dynamically run all filters or to find a particular filter for a specific configuration
     * change. All filter functions that need to be discovered and run in a generic way need to be pushed to this array.
     * The signature of all filter functions is filter(settings, htmlRoot)
     */
    filterRegistry: FilterRegistry = {
        applyAllInterlinearVariantsFilter,
        applyCharacterNormalizationFilter,
        applyCommentsFilter,
        applyCriticalApparatusFilter,
        applyCriticalApparatusHighlighter,
        applyDiacriticsModeDisplayFilter,
        applyDiacriticsModeHighlightFilter,
        applyEmphasizeInitialsFilter,
        applyExpandAbbreviationsFilter,
        applyHighlightPersonNamesFilter,
        applyHighlightPlaceNamesFilter,
        applyHighlightSubjectReferencesFilter,
        applyMedievalPunctuationFilter,
        applyLabelTitelsFilter,
        applyMisspellingsFilter,
        applyModernPunctuationFilter,
        applyOriginalCapitalizationFilter,
        applyOriginalWordBoundariesFilter,
        applySuppliedInsertFilter,
        applySuppliedMissingIllegibleFilter,
        applySwappedVersesFilter,
        applyProseVersionFilter,
        applyUncountedVersesFilter,
    };

    /* this property can take a DOM element; changes to text setting properties automatically trigger
     * a re-filtering of the DOM elements
     */
    htmlRoot: HTMLElement | null = null;

    /**
     * The settings property uses a proxy to intercept all changes to text setting configurations.
     * The set() function updates the corresponding values and calls the corresponding filter functions.
     * The mapping between configuration values and filter functions is done as follows:
     * 1) Prepend the string "apply" to the beginning of the property name
     * 2) Remove the string "display" from the start of the property name, and capitalize the first character of the remaining name
     * 3) Add the string "Filter" to the end of the property name
     *
     * This maps e.g. the "displayCriticalApparatus" property to applyCriticalApparatusFilter() or
     * "emphasizeInitials" to applyEmphasizeInitialsFilter().
     * The filter functions are then retrieved from the filterRegistry, using the name established in this way.
     */
    settings: TextSettings = new Proxy(new TextSettings(this), {
        set: (target, key, value): boolean => {
            Reflect.set(target, key, value);

            if (this.htmlRoot && typeof key === "string") {
                const functionName = mapPropertyToFunctionName(key);
                const updateFunction =
                    this.filterRegistry[functionName as keyof FilterRegistry];

                if (updateFunction) {
                    updateFunction(this.settings, this.htmlRoot);
                }
            }

            return true;
        },
    });

    applyAllFiltersToHtml(): void {
        if (!this.htmlRoot) {
            return;
        }

        Object.values(this.filterRegistry).forEach((f) => {
            f(this.settings, this.htmlRoot as HTMLElement);
        });
    }
}

/**
 * This class maintains the text display configuration settings. All its atomic properties are proxied by the TextFilter class.
 * All its object properties are proxied by the TextSettings class itself. When a setting on an object property changes, the
 * TextSettings class needs to call the filter to update the DOM tree, therefore the constructor takes an TextFilter object
 * as a parameter in order to be able to access the htmlRoot for updates.
 */
class TextSettings {
    parent: TextFilter;

    constructor(parent: TextFilter) {
        this.parent = parent;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            this.scrollSynchronously = false;
        }
    }

    characterNormalization = false;
    diacriticsModeDisplay = false;
    diacriticsModeHighlight = false;
    displayAllInterlinearVariants = false;
    displayComments = false;

    displayCriticalApparatus: CriticalApparatusSettings = new Proxy(
        { App1: false, App2: false, App3: false, App4: false },
        {
            set: (
                target: CriticalApparatusSettings,
                key: string | symbol,
                value: unknown,
            ): boolean => {
                if (typeof key === "string" && key in target) {
                    target[key as ApparatusKey] = Boolean(value);
                    if (this.parent.htmlRoot) {
                        applyCriticalApparatusFilter(
                            this,
                            this.parent.htmlRoot,
                        );
                    }
                }
                return true;
            },
        },
    );

    displayDeviationsInVariants = false;
    displayMedievalPunctuation = true;
    displayMisspellings = false;
    displayModernPunctuation = false;
    displayOriginalCapitalization = true;
    displayOriginalLineFlow = true;
    displayOriginalWordBoundaries = false;
    displayProseVersion = false;
    emphasizeInitials = true;
    expandAbbreviations = false;

    highlightCriticalApparatusReferences: HighlightCriticalApparatusSettings =
        new Proxy(
            { App1: false, App2: false, App3: false },
            {
                set: (
                    target: HighlightCriticalApparatusSettings,
                    key: string | symbol,
                    value: unknown,
                ): boolean => {
                    if (typeof key === "string" && key in target) {
                        target[key as HighlightApparatusKey] = Boolean(value);
                        if (this.parent.htmlRoot) {
                            applyCriticalApparatusHighlighter(
                                this,
                                this.parent.htmlRoot,
                            );
                        }
                    }
                    return true;
                },
            },
        );

    highlightPersonNames = false;
    highlightPlaceNames = false;
    highlightSubjectReferences = false;
    scrollSynchronously = true;
    displaySuppliedInsert = false;
    displaySuppliedMissingIllegible = false;
    displayUncountedVerses = false;
    displaySwappedVerses = true;
    displayLabelTitels = false;
}

/**
 * Filter functions for the DOM tree
 */
function applyAllInterlinearVariantsFilter(
    textSettings: TextSettings,
    htmlRoot: HTMLElement,
): void {
    const { displayAllInterlinearVariants } = textSettings;
    const interlinearNodes =
        htmlRoot.querySelectorAll<HTMLElement>("div.interlinear");

    toggleHidden(interlinearNodes, displayAllInterlinearVariants);
}

function applyCharacterNormalizationFilter(
    textSettings: TextSettings,
    htmlRoot: HTMLElement,
): void {
    const { characterNormalization } = textSettings;
    const regNodes = htmlRoot.querySelectorAll<HTMLElement>(
        "span.reg[rend^='ba_']",
    );

    regNodes.forEach((n) => {
        const firstChar = n.innerHTML.slice(0, 1);
        const isUpperCase = firstChar === firstChar.toUpperCase();
        const replacement = n.getAttribute("replacement") ?? "";
        const orig = n.getAttribute("orig") ?? "";

        if (characterNormalization) {
            n.innerHTML = isUpperCase ? replacement.toUpperCase() : replacement;
        } else {
            n.innerHTML = isUpperCase ? orig.toUpperCase() : orig;
        }
    });
}

function applyCriticalApparatusFilter(
    textSettings: TextSettings,
    htmlRoot: HTMLElement,
): void {
    const { displayCriticalApparatus } = textSettings;

    const manuscriptHeadingApp = htmlRoot.querySelectorAll<HTMLElement>(
        ".app.manuscript-heading",
    );
    const witnessListApp =
        htmlRoot.querySelectorAll<HTMLElement>(".app.witness-list");

    toggleHidden(manuscriptHeadingApp, displayCriticalApparatus.App3);
    toggleHidden(witnessListApp, displayCriticalApparatus.App2);

    for (let i = 1 as 1 | 2 | 3 | 4; i <= 4; i++) {
        const appNodes = htmlRoot.querySelectorAll<HTMLElement>(
            `.app[n='${i}']`,
        );
        toggleHidden(appNodes, displayCriticalApparatus[`App${i}`]);
    }
}

function applyCommentsFilter(
    textSettings: TextSettings,
    htmlRoot: HTMLElement,
): void {
    const { displayComments } = textSettings;
    const commentNodes = htmlRoot.querySelectorAll<HTMLElement>(
        "div.body > .note, div.body > .commentary-container",
    );

    toggleHidden(commentNodes, displayComments);
}

function applyCriticalApparatusHighlighter(
    textSettings: TextSettings,
    htmlRoot: HTMLElement,
): void {
    const { highlightCriticalApparatusReferences } = textSettings;

    for (const key of Object.keys(
        highlightCriticalApparatusReferences,
    ) as HighlightApparatusKey[]) {
        const appLevel = key.substring(3);

        if (!highlightCriticalApparatusReferences[key]) {
            htmlRoot
                .querySelectorAll<HTMLElement>(`.highlight-appref-${appLevel}`)
                .forEach((n) => n.replaceWith(...Array.from(n.childNodes)));
        } else {
            htmlRoot
                .querySelectorAll<HTMLElement>(`.app[n = "${appLevel}"]`)
                .forEach((node) => {
                    const locAttr = node.getAttribute("loc");
                    const nodeLevel = node.getAttribute("n");

                    if (!locAttr || !nodeLevel) {
                        return;
                    }

                    const locs = locAttr.split(" ");

                    const parsedLocs = locs
                        .map((loc) =>
                            loc.match(/([0-9]+):([0-9]+)(-([0-9]+):([0-9]+))?/),
                        )
                        .filter(
                            (match): match is RegExpMatchArray =>
                                match !== null,
                        );

                    log(locs);
                    log(parsedLocs);

                    const filteredLocs = parsedLocs
                        .filter(
                            (parsedLoc) =>
                                parsedLoc[1] ===
                                (parsedLoc[4] ? parsedLoc[4] : parsedLoc[1]),
                        )
                        .sort(
                            (a, b) =>
                                parseInt(a[1], 10) -
                                parseInt(b[1], 10) +
                                parseInt(a[2], 10) -
                                parseInt(b[2], 10),
                        );

                    const reducedLocs = filteredLocs.reduce((acc, loc) => {
                        const line = loc[1];
                        if (!acc.has(line)) {
                            acc.set(line, new Set<number>());
                        }

                        const start = parseInt(loc[2], 10);
                        const end = parseInt(loc[5] ?? loc[2], 10);

                        for (let i = start; i <= end; i++) {
                            acc.get(line)?.add(i);
                        }

                        return acc;
                    }, new Map<string, Set<number>>());

                    reducedLocs.forEach((wordIndices, lineIndex) => {
                        const line = htmlRoot.querySelector<HTMLElement>(
                            `div.l[n='${lineIndex}'] > div[data-type='line-content'] > div`,
                        );

                        if (!line) {
                            log(`No line found for ${lineIndex}`);
                            return;
                        }

                        const innerHtml = line.innerHTML
                            .toString()
                            .replace(/\s+/g, " ")
                            .split(/\s+(?![^<]*>|[^<>]*<\/)/);

                        const newInnerHtml = innerHtml.map(
                            (token, tokenIndex) => {
                                if (wordIndices.has(tokenIndex + 1)) {
                                    return `<span class="highlight-appref-${nodeLevel}" n="${nodeLevel}">${token}</span>`;
                                }
                                return token;
                            },
                        );

                        line.innerHTML = newInnerHtml.join(" ");
                    });
                });
        }
    }
}

function applyDiacriticsModeDisplayFilter(
    textSettings: TextSettings,
    htmlRoot: HTMLElement,
): void {
    const { diacriticsModeDisplay } = textSettings;

    const diacriticsNodes = htmlRoot.querySelectorAll<HTMLElement>(
        "span.choice[n = 'choice']",
    );

    diacriticsNodes.forEach((n) => {
        toggleHidden(
            n.querySelectorAll<HTMLElement>("span.orig"),
            !diacriticsModeDisplay,
        );
        toggleHidden(
            n.querySelectorAll<HTMLElement>("span.reg:not([rend^='ba_'])"),
            diacriticsModeDisplay,
        );
        toggleHidden(
            n.querySelectorAll<HTMLElement>("span.sic"),
            diacriticsModeDisplay,
        );
    });

    const conjectureNodes =
        htmlRoot.querySelectorAll<HTMLElement>(".choice.conjecture");

    conjectureNodes.forEach((n) => {
        toggleHidden(
            n.querySelectorAll<HTMLElement>(":scope > .orig"),
            !diacriticsModeDisplay,
        );

        toggleHidden(
            n.querySelectorAll<HTMLElement>(":scope > .reg"),
            diacriticsModeDisplay,
        );
    });
}

function applyLabelTitelsFilter(
    textSettings: TextSettings,
    htmlRoot: HTMLElement,
): void {
    const { displayLabelTitels } = textSettings;
    const labelTitelsNodes = htmlRoot.querySelectorAll<HTMLElement>(
        ".label[type='Zwischenüberschrift']",
    );
    toggleLabels(labelTitelsNodes, displayLabelTitels);
}

function applyDiacriticsModeHighlightFilter(
    textSettings: TextSettings,
    htmlRoot: HTMLElement,
): void {
    const { diacriticsModeHighlight } = textSettings;

    const diacriticsNodes = htmlRoot.querySelectorAll<HTMLElement>(
        "span.choice[n = 'choice']",
    );

    toggleHighlighted(diacriticsNodes, diacriticsModeHighlight);
}

/**
 * Moves the initial character to a sibling position with the current line and applies the .drop-caps CSS class, if the emphasizeInitials
 * text setting is switched on; moves the initial character back to its in-line position and removes the .drop-caps CSS class, if not
 */
function applyEmphasizeInitialsFilter(
    textSettings: TextSettings,
    htmlRoot: HTMLElement,
): void {
    const { emphasizeInitials } = textSettings;
    const initialsNodes = htmlRoot.querySelectorAll<HTMLElement>(
        "span.hi[rend = 'initial']",
    );

    initialsNodes.forEach((n) => {
        if (emphasizeInitials) {
            n.classList.add("drop-caps");
        } else {
            n.classList.remove("drop-caps");
        }
    });
}

function applyExpandAbbreviationsFilter(
    textSettings: TextSettings,
    htmlRoot: HTMLElement,
): void {
    const { expandAbbreviations } = textSettings;
    const expansionNodes = htmlRoot.querySelectorAll<HTMLElement>("span.ex");

    handleReplacements(expansionNodes, expandAbbreviations);
}

function applyHighlightPersonNamesFilter(
    textSettings: TextSettings,
    htmlRoot: HTMLElement,
): void {
    const { highlightPersonNames } = textSettings;
    const personNodes = htmlRoot.querySelectorAll<HTMLElement>("span.persName");

    toggleHighlighted(personNodes, highlightPersonNames);
}

function applyHighlightPlaceNamesFilter(
    textSettings: TextSettings,
    htmlRoot: HTMLElement,
): void {
    const { highlightPlaceNames } = textSettings;
    const placeNodes = htmlRoot.querySelectorAll<HTMLElement>("span.placeName");

    toggleHighlighted(placeNodes, highlightPlaceNames);
}

function applyHighlightSubjectReferencesFilter(
    textSettings: TextSettings,
    htmlRoot: HTMLElement,
): void {
    const { highlightSubjectReferences } = textSettings;
    const subjectNodes = htmlRoot.querySelectorAll<HTMLElement>("span.term");

    toggleHighlighted(subjectNodes, highlightSubjectReferences);
}

function applyMedievalPunctuationFilter(
    textSettings: TextSettings,
    htmlRoot: HTMLElement,
): void {
    const { displayMedievalPunctuation } = textSettings;

    const medievalPunctuationNodes = Array.from(
        htmlRoot.querySelectorAll<HTMLElement>(
            "span.medieval-punctuation, span.enjamb",
        ),
    ).filter((el) => {
        return !el.closest(".commentary-container") && !el.closest(".note");
    });

    toggleHidden(medievalPunctuationNodes, displayMedievalPunctuation);
}

function applyMisspellingsFilter(
    textSettings: TextSettings,
    htmlRoot: HTMLElement,
): void {
    const { displayMisspellings } = textSettings;
    const misspelledNodes = htmlRoot.querySelectorAll<HTMLElement>("span.corr");

    handleReplacements(misspelledNodes, displayMisspellings);
}

function applyModernPunctuationFilter(
    textSettings: TextSettings,
    htmlRoot: HTMLElement,
): void {
    const { displayModernPunctuation } = textSettings;

    const modernPunctuationNodes = Array.from(
        htmlRoot.querySelectorAll<HTMLElement>("span.modern-punctuation"),
    ).filter((el) => !el.closest(".figDesc") && !el.closest(".note"));

    const indentNodes = htmlRoot.querySelectorAll<HTMLElement>(
        "div.l[rend='indent']",
    );

    const lineBreakNodes =
        htmlRoot.querySelectorAll<HTMLElement>(".line-break");

    toggleHidden(modernPunctuationNodes, displayModernPunctuation);
    toggleHidden(lineBreakNodes, !displayModernPunctuation);
    toggleIndented(indentNodes, displayModernPunctuation);
}

function applyOriginalCapitalizationFilter(
    textSettings: TextSettings,
    htmlRoot: HTMLElement,
): void {
    const { displayOriginalCapitalization } = textSettings;
    let capitalizationNodes = htmlRoot.querySelectorAll<HTMLElement>(
        "span.hi[rend='Kapital']",
    );

    capitalizationNodes.forEach((n) => {
        let outerHtml = n.outerHTML.toString();
        let firstChar = "";

        const match = outerHtml.match(/(\w)(<|(?![^<]*>|[^<>]*<\/))/);
        if (!match) {
            return;
        }

        if (displayOriginalCapitalization) {
            firstChar = match[1].toUpperCase();
        } else {
            firstChar = match[1].toLowerCase();
        }

        outerHtml = outerHtml.replace(
            /(\w)(<|(?![^<]*>|[^<>]*<\/))/,
            `${firstChar}$2`,
        );
        n.outerHTML = outerHtml;
    });

    capitalizationNodes = htmlRoot.querySelectorAll<HTMLElement>(
        ".text-group-title:not(:empty)",
    );

    capitalizationNodes.forEach((n) => {
        let outerHtml = n.outerHTML.toString();
        const firstChars = outerHtml.match(/>([A-Za-z])/);
        if (!firstChars) {
            return;
        }

        let firstChar = firstChars[1];
        if (!firstChar) {
            return;
        }

        if (!displayOriginalCapitalization) {
            firstChar = firstChar.toUpperCase();
        }

        outerHtml = outerHtml.replace(/>([A-Za-z])/, `>${firstChar}`);
        n.outerHTML = outerHtml;
    });
}

function applyOriginalWordBoundariesFilter(
    textSettings: TextSettings,
    htmlRoot: HTMLElement,
): void {
    const { displayOriginalWordBoundaries } = textSettings;
    const blankNodes = htmlRoot.querySelectorAll<HTMLElement>(
        "span.reg[rend='blank']",
    );
    const noBlankNodes = htmlRoot.querySelectorAll<HTMLElement>(
        "span.reg[rend='no_blank']",
    );

    handleReplacements(blankNodes, !displayOriginalWordBoundaries);
    handleReplacements(noBlankNodes, !displayOriginalWordBoundaries);
}

function applyProseVersionFilter(
    _textSettings: TextSettings,
    _htmlRoot: HTMLElement,
): void {
    // no operation needed, this is taken care of elsewhere
}

function applySuppliedInsertFilter(
    textSettings: TextSettings,
    htmlRoot: HTMLElement,
): void {
    const { displaySuppliedInsert } = textSettings;
    const suppliedNodes = htmlRoot.querySelectorAll<HTMLElement>(
        `span.supplied[reason='insert'],
        span.supplied[reason='false'],
        span.supplied[reason='edbindestrich']`,
    );

    toggle(suppliedNodes, displaySuppliedInsert, "show-markers");
}

function applySuppliedMissingIllegibleFilter(
    textSettings: TextSettings,
    htmlRoot: HTMLElement,
): void {
    const { displaySuppliedMissingIllegible } = textSettings;

    const suppliedNodes = Array.from(
        htmlRoot.querySelectorAll<HTMLElement>(
            "span.supplied[reason='illegible'], span.supplied[reason='missing']",
        ),
    );

    toggleHidden(suppliedNodes, displaySuppliedMissingIllegible);
}

function applySwappedVersesFilter(
    textSettings: TextSettings,
    htmlRoot: HTMLElement,
): void {
    const { displaySwappedVerses } = textSettings;

    const swappedLines = Array.from(
        htmlRoot.querySelectorAll<HTMLElement>(".l[rend^='n(']"),
    );

    const handled = new Set<HTMLElement>();

    swappedLines.forEach((line) => {
        if (handled.has(line)) {
            return;
        }

        const parent = line.parentElement;
        const currentVerseNumber = line.getAttribute("n");
        const rend = line.getAttribute("rend");
        const match = rend?.match(/\((\d+)\)/);

        if (!parent || !currentVerseNumber || !match) {
            return;
        }

        const targetVerseNumber = match[1];

        const targetLine = htmlRoot.querySelector<HTMLElement>(
            `.l[n='${CSS.escape(targetVerseNumber)}']`,
        );

        if (!targetLine || targetLine.parentElement !== parent) {
            return;
        }

        const lines = [line, targetLine];

        lines.forEach((el) => handled.add(el));

        const firstLineInDom = [...lines].sort((a, b) => {
            return a.compareDocumentPosition(b) &
                Node.DOCUMENT_POSITION_FOLLOWING
                ? -1
                : 1;
        })[0];

        const marker = document.createComment("swapped-lines-marker");
        parent.insertBefore(marker, firstLineInDom);

        const sortedLines = [...lines].sort((a, b) => {
            const aCurrent = Number(a.getAttribute("n"));
            const bCurrent = Number(b.getAttribute("n"));

            const aRend = a.getAttribute("rend")?.match(/\((\d+)\)/)?.[1];
            const bRend = b.getAttribute("rend")?.match(/\((\d+)\)/)?.[1];

            const aOrder = displaySwappedVerses
                ? Number(aRend ?? aCurrent)
                : aCurrent;

            const bOrder = displaySwappedVerses
                ? Number(bRend ?? bCurrent)
                : bCurrent;

            return aOrder - bOrder;
        });

        sortedLines.forEach((sortedLine) => {
            sortedLine.classList.toggle(
                "is-originalOrder",
                displaySwappedVerses,
            );

            getLineBlock(sortedLine).forEach((node) => {
                parent.insertBefore(node, marker);
            });
        });

        marker.remove();
    });
}

function getLineBlock(line: HTMLElement): HTMLElement[] {
    const block: HTMLElement[] = [line];

    let next = line.nextElementSibling as HTMLElement | null;

    while (
        next &&
        (next.matches(".interlinear") ||
            next.matches(".app") ||
            next.matches(".note"))
    ) {
        block.push(next);
        next = next.nextElementSibling as HTMLElement | null;
    }

    return block;
}

function applyUncountedVersesFilter(
    textSettings: TextSettings,
    htmlRoot: HTMLElement,
): void {
    const { displayUncountedVerses } = textSettings;
    const uncountedVerseNodes = htmlRoot.querySelectorAll<HTMLElement>(
        ".l[n='-']:not([rend='Fragment']), .seg[type='repetition']",
    );

    toggleHidden(uncountedVerseNodes, displayUncountedVerses);
}

function handleReplacements(
    nodeList: Iterable<HTMLElement>,
    shallReplace: boolean,
): void {
    for (const n of nodeList) {
        if (shallReplace) {
            n.classList.add("replacement");
            n.innerHTML = n.getAttribute("replacement") ?? "";
        } else {
            n.classList.remove("replacement");
            n.innerHTML = n.getAttribute("orig") ?? "";
        }
    }
}

function mapPropertyToFunctionName(propertyName: string): string {
    const filterName = propertyName.replace(/^display/, "") + "Filter";
    return "apply" + filterName.charAt(0).toUpperCase() + filterName.slice(1);
}

function toggle(
    nodeList: Iterable<Element>,
    addClass: boolean,
    cssClass: string,
): void {
    for (const n of nodeList) {
        if (addClass) {
            n.classList.add(cssClass);
        } else {
            n.classList.remove(cssClass);
        }
    }
}

function toggleHidden(nodeList: Iterable<Element>, display: boolean): void {
    toggle(nodeList, !display, "hidden");
}

function toggleHighlighted(
    nodeList: Iterable<Element>,
    highlight: boolean,
): void {
    toggle(nodeList, highlight, "is-highlighted");
}

function toggleIndented(nodeList: Iterable<Element>, indent: boolean): void {
    toggle(nodeList, indent, "is-indented");
}

function toggleLabels(nodeList: Iterable<Element>, display: boolean): void {
    toggle(nodeList, display, "is-label");
}

export { TextFilter, TextSettings };
export type {
    TextFilterFunction,
    FilterRegistry,
    CriticalApparatusSettings,
    HighlightCriticalApparatusSettings,
};
